import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { FlaskConical, Plus, Trophy, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useABTests, useCreateABTest, useSkills, useCurrentOrgId } from "@/hooks/useSkillOS";

export default function ABTestPanel() {
  const [open, setOpen] = useState(false);
  const { data: tests = [] } = useABTests();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Teste variações de prompts para otimizar performance.</p>
        <CreateABTestDialog open={open} setOpen={setOpen} />
      </div>

      {tests.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Nenhum teste A/B criado. Crie um para começar a otimizar prompts.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tests.map((test) => (
            <Card key={test.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold">{test.skill_name}</CardTitle>
                  <Badge variant={test.status === "running" ? "default" : "secondary"}>
                    {test.status === "running" ? "Em andamento" : test.status === "completed" ? "Concluído" : test.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <VariantRow
                  label={test.variant_a_name}
                  executions={test.variant_a_executions}
                  successRate={test.variant_a_success_rate ?? 0}
                  quality={test.variant_a_avg_quality ?? 0}
                  isWinner={test.winner === "A"}
                  total={test.sample_size}
                />
                <VariantRow
                  label={test.variant_b_name}
                  executions={test.variant_b_executions}
                  successRate={test.variant_b_success_rate ?? 0}
                  quality={test.variant_b_avg_quality ?? 0}
                  isWinner={test.winner === "B"}
                  total={test.sample_size}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function VariantRow({ label, executions, successRate, quality, isWinner, total }: {
  label: string; executions: number; successRate: number; quality: number; isWinner: boolean; total: number;
}) {
  return (
    <div className={`p-2 rounded-md ${isWinner ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-muted/50"}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium">{label}</span>
          {isWinner && <Trophy className="w-3 h-3 text-emerald-500" />}
        </div>
        <span className="text-xs text-muted-foreground">{executions}/{total}</span>
      </div>
      <Progress value={(executions / Math.max(1, total)) * 100} className="h-1 mb-1" />
      <div className="flex gap-3 text-xs text-muted-foreground">
        <span>Sucesso: {Math.round(successRate * 100)}%</span>
        <span>Qualidade: {Math.round(quality)}/100</span>
      </div>
    </div>
  );
}

function CreateABTestDialog({ open, setOpen }: { open: boolean; setOpen: (v: boolean) => void }) {
  const { toast } = useToast();
  const { data: skills = [] } = useSkills();
  const { data: orgId } = useCurrentOrgId();
  const create = useCreateABTest();
  const [skillName, setSkillName] = useState("");
  const [promptA, setPromptA] = useState("");
  const [promptB, setPromptB] = useState("");

  const submit = () => {
    if (!orgId) {
      toast({ title: "Erro", description: "Sem organização", variant: "destructive" });
      return;
    }
    create.mutate(
      { skill_name: skillName, org_id: orgId, variant_a_prompt: promptA, variant_b_prompt: promptB },
      {
        onSuccess: () => {
          toast({ title: "Teste A/B criado" });
          setOpen(false);
          setSkillName(""); setPromptA(""); setPromptB("");
        },
        onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="w-4 h-4 mr-1" /> Novo Teste A/B</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar Teste A/B</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Skill</label>
            <Select value={skillName} onValueChange={setSkillName}>
              <SelectTrigger><SelectValue placeholder="Selecione uma skill do registry" /></SelectTrigger>
              <SelectContent>
                {skills.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Prompt A (Controle)</label>
            <Textarea value={promptA} onChange={(e) => setPromptA(e.target.value)} rows={3} />
          </div>
          <div>
            <label className="text-sm font-medium">Prompt B (Variante)</label>
            <Textarea value={promptB} onChange={(e) => setPromptB(e.target.value)} rows={3} />
          </div>
          <Button onClick={submit} disabled={create.isPending || !skillName || !promptA || !promptB} className="w-full">
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Criar Teste
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
